import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { LivingSituationType } from 'src/app/services/dropdowns/dropdowns.model';
import {
  FinalLoanApplication,
  ApplicantOwnAnyVehicles,
  AddressOwn,
  AddressParent,
  AddressRent,
  ApplicantVehicle,
} from 'src/app/services/loan/loan.model';
import { LoanService } from 'src/app/services/loan/loan.service';

/**
 * The parent {FormGroup} must contain the following controls.
 * * HousingContactName
 * * HousingContactPhone
 * * MonthlyCost
 * * MortgageBalance
 * * Lienholder
 * * MarketValue
 * * ApplicantVehicles
 *
 * Make sure to set up {ApplicantVehicles} as a new {FormArray}:
 * ```
 * ApplicantVehicles: this.formBuilder.array([]),
 * ```
 */
@Component({
  selector: 'app-rent-vehicle-group',
  templateUrl: './rent-vehicle-group.component.html',
  styleUrls: ['./rent-vehicle-group.component.scss'],
})
export class RentVehicleGroupComponent implements OnInit, OnDestroy {
  @Input() controls: {
    [key: string]: AbstractControl;
  } = {};

  vehicleFormGroup: FormGroup;
  loanApplication: FinalLoanApplication | null = null;
  vehiclesOwned: ApplicantOwnAnyVehicles | null = null;
  coApplicant = false;

  private subscriptions: Array<Subscription> = [];

  get applicantVehicles() {
    if (this.controls && this.controls['ApplicantVehicles']) {
      return this.controls['ApplicantVehicles'] as FormArray;
    }

    return null;
  }

  /**
   * Gets the current year plus one.
   */
  get year(): number {
    const current = new Date();
    return current.getFullYear() + 1;
  }

  constructor(
    private loanService: LoanService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    this.vehicleFormGroup = this.formBuilder.group({});
    this.coApplicant = this.router.url.includes('co-applicant');
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.loanService.finalLoanApplication.subscribe(
        (data: FinalLoanApplication | null) => {
          if (data) {
            this.loanApplication = { ...data };

            const addressKey = this.coApplicant
              ? 'CoApplicantAddress'
              : 'ApplicantAddress';

            const housingKey = this.coApplicant
              ? 'CoApplicantHousing'
              : 'ApplicantHousing';

            const livingSituation = data[addressKey]?.LivingSituation;

            if (data[addressKey] && livingSituation) {
              const emptyFormValues = this.getKeysToEnable(livingSituation);

              // If form data exists use it otherwise set to empty form values.
              this.setFormValues(
                data[housingKey] || emptyFormValues,
                Object.keys(emptyFormValues)
              );
            }
          }
        }
      )
    );

    this.subscriptions.push(
      this.loanService[
        this.coApplicant
          ? 'CoApplicantOwnAnyVehicles'
          : 'ApplicantOwnAnyVehicles'
      ].subscribe((data: ApplicantOwnAnyVehicles | null) => {
        if (data) {
          this.vehiclesOwned = { ...data };
          this.setVehicleFormArray();
        } else {
          this.vehiclesOwned = null;
        }
      })
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Each living situation selection has it's own set of controls to display.
   *
   * @param livingSituation The living situation selection.
   * @returns Object with empty values that'll be enabled on the FormGroup.
   */
  getKeysToEnable(
    livingSituation: string
  ): AddressRent | AddressOwn | AddressParent {
    let keys: AddressRent | AddressOwn | AddressParent;

    // Set empty form based on living situation selection.
    if (livingSituation === LivingSituationType.rent) {
      keys = {
        HousingContactName: '',
        HousingContactPhone: '',
        MonthlyCost: '',
      };
    } else if (livingSituation === LivingSituationType.own) {
      keys = {
        MortgageBalance: '',
        Lienholder: '',
        MarketValue: '',
      };
    } else {
      keys = {
        MonthlyCost: '',
      };
    }

    return keys;
  }

  getFormControl(key: string): FormControl {
    return this.controls[key] as FormControl;
  }

  setFormValues(data: any, keysToEnable: Array<string>) {
    // Set form values
    Object.keys(data).forEach((key) => {
      // Check if key exists on current form.
      if (keysToEnable.includes(key)) {
        const control = this.getFormControl(key);

        if (control) {
          control.enable();
          control.setValue(data?.[key]);
        }
      }
    });
  }

  setVehicleFormArray() {
    // Initilize vehicle FormArray.
    for (let index = 0; index < this.vehiclesOwned!.NumberOfVehicles; index++) {
      this.vehicleFormGroup = this.formBuilder.group({
        Year: [
          '',
          [
            Validators.required,
            Validators.min(1900),
            Validators.max(this.year),
          ],
        ],
        Make: ['', Validators.required],
        Model: ['', Validators.required],
        Mileage: [
          '',
          [Validators.required, Validators.min(0), Validators.max(500000)],
        ],
        Lienholder: [''],
        BalanceOwed: ['', Validators.min(0)],
        Payments: [{ value: '', disabled: true }],
      });

      this.applicantVehicles?.push(this.vehicleFormGroup);
    }

    // Set values for vehicle FormArray, if applicable.
    if (
      this.loanApplication &&
      this.loanApplication[
        this.coApplicant ? 'CoApplicantVehicles' : 'ApplicantVehicles'
      ]
    ) {
      const vehicles =
        this.loanApplication[
          this.coApplicant ? 'CoApplicantVehicles' : 'ApplicantVehicles'
        ];

      if (vehicles && vehicles.length > 0) {
        vehicles.forEach((vehicle: ApplicantVehicle, i: number) => {
          Object.keys(vehicle).forEach((key) => {
            // Check if key exists on current form.
            const controlKey = this.applicantVehicles?.controls[i].get(key);

            if (controlKey) {
              controlKey.setValue(vehicle[key]);
            }
          });
        });
      }
    }
  }
}
